import { motion } from "framer-motion";
import { Flag, Gamepad2, Coffee } from "lucide-react";

const EXPERIENCES = [
  {
    title: "Go-Karting",
    description: "A sprawling track with long straights and technical turns. Speeds up to 55 km/h for the ultimate racing thrill.",
    icon: Flag,
    image: "/go-kart-experience.jpg",
    featured: true,
  },
  {
    title: "Arcade Zone",
    description: "Retro and modern games under neon lights. Compete with friends and family.",
    icon: Gamepad2,
    image: "/arcade-experience.jpg",
  },
  {
    title: "Café",
    description: "Recharge with freshly brewed coffee and snacks. The perfect pit stop.",
    icon: Coffee,
    image: "/cafe.jpg",
  },
];

function ExperienceCard({ item, index }) {
  const Icon = item.icon;

  return (
    <motion.div
      data-testid={`experience-card-${index}`}
      className={`relative overflow-hidden group cursor-pointer ${
        item.featured
          ? "col-span-1 md:col-span-2 row-span-2 min-h-[400px] md:min-h-[500px]"
          : "min-h-[240px]"
      }`}
      initial={{ opacity: 0, y: 60, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: item.image.includes("unsplash.com")
            ? `url(${item.image}&auto=format&fit=crop&q=75&w=800)`
            : item.image.includes("pexels.com")
            ? `url(${item.image})`
            : `url(${item.image})`
        }}
        whileHover={{ scale: 1.08 }}
        transition={{ duration: 0.6 }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 group-hover:from-black/90 transition-all duration-500" />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-[#e8001d]/10 to-transparent" />

      <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
        <motion.div
          className="flex items-center gap-3 mb-3"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 + index * 0.1 }}
        >
          <div className="w-10 h-10 flex items-center justify-center bg-[#e8001d]/20 border border-[#e8001d]/30 backdrop-blur-sm">
            <Icon size={20} className="text-[#e8001d]" />
          </div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#e8001d] font-bold">
            {item.featured ? "Flagship" : "Experience"}
          </span>
        </motion.div>
        <h3 className="font-heading text-[#f5f5f5] text-3xl sm:text-4xl uppercase mb-2">
          {item.title}
        </h3>
        <p className="text-[#a3a3a3] text-sm sm:text-base leading-relaxed max-w-md">
          {item.description}
        </p>
      </div>

      {/* Red bottom line on hover */}
      <div className="absolute bottom-0 left-0 w-0 group-hover:w-full h-[2px] bg-[#e8001d] transition-all duration-500" />
    </motion.div>
  );
}

export default function Experiences() {
  return (
    <section id="experiences" data-testid="experiences-section" className="py-24 sm:py-32 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#e8001d] font-bold mb-4 block"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            What Awaits You
          </motion.span>
          <motion.h2
            className="font-heading text-[#f5f5f5] text-5xl sm:text-7xl uppercase leading-none"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            The Experiences
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {EXPERIENCES.map((item, i) => (
            <ExperienceCard key={item.title} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
